import { enableProdMode, APP_INITIALIZER, LOCALE_ID, importProvidersFrom } from '@angular/core';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { SettingsEffects } from './app/store/settings/settings.effects';
import { MetadataEffects } from '@rillion/store/metadata/metadata.effects';
import { AuthEffects } from './app/store/auth/auth.effects';
import { EffectsModule } from '@ngrx/effects';
import { reducers } from './app/store';
import { StoreModule } from '@ngrx/store';
import { RouterSerializer } from './app/store/router/router-serializer';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { ToastrModule } from 'ngx-toastr';
import { IonicModule } from '@ionic/angular';
import { AppRoutingModule } from './app/app-routing.module';
import { CoreModule } from '@rillion/core/core.module';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { TranslateModule, TranslateCompiler, TranslateLoader } from '@ngx-translate/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { AppInitializerService } from '@rillion/core/initializer/app-initializer.service';
import { Configuration, ApiModule } from './libs/role';
import { AppSettingsService, AppSettings, JwtInterceptor, HttpErrorInterceptor } from './app/core';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { AuthConfig, OAuthService, OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import { AuthApiService } from '@rillion/auth';
import { firstValueFrom } from 'rxjs';

export class CustomTranslateHttpLoader implements TranslateLoader {
  constructor(
    private appSettingsService: AppSettingsService,
    private http: HttpClient,
    public suffix = '.json',
  ) {}

  /**
   * Gets the translations from the server and filters away missing translations.
   * This will cause ngx-translate to fall back to the default language (en) for missing translations.
   */
  getTranslation(lang: string): Observable<Object> {
    const prefix = this.appSettingsService.appSettings?.translationsUrl;
    return this.http.get<Record<string, string>>(`${prefix}${lang}${this.suffix}`).pipe(
      map(
        (translations) =>
          Object.keys(translations).reduce(
            (filtered, cur) => {
              if (translations[cur] !== '') {
                filtered[cur] = translations[cur];
              }
              return filtered;
            },
            {} as Record<string, string>,
          ) as Object,
      ),
    );
  }
}

function storageFactory(): OAuthStorage {
  return localStorage;
}

function appSettingsFactory(appSettingsService: AppSettingsService, appInitializerService: AppInitializerService, authApiService: AuthApiService, oauthService: OAuthService) {
  return () =>
    firstValueFrom(appSettingsService.loadAppSettings())
      .then((appSettings: AppSettings): AuthConfig => {
        return {
          issuer: appSettings?.authority,
          redirectUri: window.location.origin,
          clientId: appSettings?.clientId,
          responseType: 'code',
          scope: 'openid profile email offline_access',
          showDebugInformation: !environment.production,
          clearHashAfterLogin: false,
          sessionChecksEnabled: true, // enables single sign-out
          preserveRequestedRoute: true, // enables deep linking
          nonceStateSeparator: 'semicolon',
          useSilentRefresh: true,
        };
      })
      .then((authConfig: AuthConfig) => oauthService.configure(authConfig))
      .then(() => authApiService.initialLogin())
      .then(() => appInitializerService.onAppInit());
}

function configurationFactory(appSettingsService: AppSettingsService) {
  const config = new Configuration();
  Object.defineProperty(config, 'basePath', {
    get() {
      return appSettingsService.appSettings?.baseUrl;
    },
    set() {},
  });
  return config;
}

function createTranslateLoader(http: HttpClient, appSettingsService: AppSettingsService) {
  return new CustomTranslateHttpLoader(appSettingsService, http, '.json');
}

const MY_DATE_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY', // this is how your date will be parsed from Input
  },
  display: {
    dateInput: 'DD/MM/YYYY', // this is how your date will get displayed on the Input
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideHttpClient(),
    provideOAuthClient(),
    importProvidersFrom(
      ApiModule,
      BrowserModule,
      TranslateModule.forRoot({
        compiler: {
          provide: TranslateCompiler,
          useClass: TranslateMessageFormatCompiler,
        },
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient, AppSettingsService],
        },
        useDefaultLang: true,
      }),
      CoreModule,
      AppRoutingModule,
      IonicModule.forRoot(),
      ToastrModule.forRoot({
        maxOpened: 5,
        autoDismiss: true,
        timeOut: 5000,
        preventDuplicates: true,
        countDuplicates: true,
        resetTimeoutOnDuplicate: false,
      }),
      // NgRx
      StoreRouterConnectingModule.forRoot({ serializer: RouterSerializer }),
      StoreModule.forRoot(reducers, {
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
        },
      }),
      EffectsModule.forRoot([AuthEffects, MetadataEffects, SettingsEffects]),
      environment.production ? [] : StoreDevtoolsModule.instrument({ connectInZone: true }),
    ),
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: appSettingsFactory,
      deps: [AppSettingsService, AppInitializerService, AuthApiService, OAuthService, OAuthStorage],
      multi: true,
    },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: LOCALE_ID, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: Configuration, useFactory: configurationFactory, deps: [AppSettingsService] },
    { provide: OAuthStorage, useFactory: storageFactory },
    provideAnimations(),
  ],
}).catch((err) => console.error(err));
